import { getNestedGroupsIds } from "@features/builder/graph/changeGroup/moveToGroup"
import { createEmptyNode, findMaxId } from "@features/builder/graph/helpers"
import {
  AppNode,
  CategoricalNode,
  DiscreteNode,
  GroupNode,
  NodeType,
} from "@features/builder/graph/types"
import { LAG_LABEL_REGEX, getLabelWithoutLagSuffix } from "@utils/node/labelValidation"
import { GraphNodeData, TKNode } from "@store/services/converter/types"
import { DfCpdRow, dfCpdToRows, getDistributionRowPossibleValues } from "@utils/node/dfCpd"
import logger from "@utils/logger"

export type { DfCpdRow } from "@utils/node/dfCpd"
export { dfCpdToRows, rowsToDfCpd } from "@utils/node/dfCpd"
export { createLagNodeLabel } from "@utils/node/labelValidation"
import { XYPosition } from "@xyflow/react"
import { getParentsList } from "@utils/node/getParentsList"

export const areNodesUsingTheSameUnit = (nodesData: GraphNodeData[]) => {
  if (nodesData.length === 0) return true

  return nodesData.every(
    ({ display }) => display["Unit Format"] === nodesData[0]?.display["Unit Format"],
  )
}

export const isLagNodeWithCorrespondingNode = (node: GraphNodeData, allNodesLabels: string[]) => {
  const isLagNode = LAG_LABEL_REGEX.test(node.display.label)

  if (!isLagNode) return false

  const labelWithoutLag = getNonLagNodeLabel(node.display.label)

  return allNodesLabels.includes(labelWithoutLag)
}

export const getNonLagNodeLabel = (label: string) => {
  const labelWithoutLag = getLabelWithoutLagSuffix(label)

  return labelWithoutLag.endsWith("_")
    ? labelWithoutLag.slice(0, -1).trim()
    : labelWithoutLag.trim()
}

export const getLagCorrespondingNode = (node: GraphNodeData, allNodes: GraphNodeData[]) => {
  const labelWithoutLag = getNonLagNodeLabel(node.display.label)

  return allNodes.find((n) => n.display.label === labelWithoutLag)
}

export const createNodeInGroup = (
  allNodes: AppNode[],
  group: GroupNode,
  type: NodeType,
  position: XYPosition,
) => {
  const addingGroup = type === NodeType.Group
  const maxId = findMaxId(allNodes, addingGroup)

  const newNode = createEmptyNode(type, maxId, position, false)
  newNode.parentId = group.id
  newNode.extent = "parent"
  newNode.data.nestingLevel = (group.data.nestingLevel || 1) + 1

  if (addingGroup) {
    ;(newNode as GroupNode).data.metaGroup.parent = group.id
  } else {
    ;(newNode as Exclude<AppNode, GroupNode>).data.metaNode.groups = getNestedGroupsIds(
      group,
      allNodes,
    )
  }

  return newNode
}

export const createNodeLabelMap = (nodes: AppNode[]): Record<string, string> => {
  return nodes.reduce((map: Record<string, string>, node) => {
    map[node.id] = node.data.label ?? node.id
    return map
  }, {})
}

export const createNodeTypeMap = (nodes: AppNode[]): Record<string, NodeType> => {
  return nodes.reduce((map: Record<string, NodeType>, node) => {
    map[node.id] = node.type
    return map
  }, {})
}

export const getNodePossibleValues = (node: AppNode, linkedNode?: TKNode | undefined): string[] => {
  switch (node.type) {
    case NodeType.Categorical:
      return getCategoricalNodeParameters(node, linkedNode)
    case NodeType.Discrete:
      return getDiscreteNodePossibleValues(node, linkedNode)
    default:
      return []
  }
}

const getDiscreteNodePossibleValues = (node: DiscreteNode, linkedNode: TKNode | undefined) => {
  let dfCpd = node.data.originalNode.properties.df_cpd

  if (linkedNode) {
    if ("df_cpd" in linkedNode.properties) {
      dfCpd = linkedNode.properties.df_cpd
    } else {
      logger.error(
        `Linked node with id ${linkedNode.name} does not have df_cpd property for node ${node.id}`,
      )
      return []
    }
  }

  const setOfPossibleValues = new Set<string>()
  const rows = dfCpdToRows(dfCpd)

  rows.forEach((row) => {
    const rowPossibleValues = getDistributionRowPossibleValues(row)
    rowPossibleValues.forEach((value) => setOfPossibleValues.add(value))
  })

  return Array.from(setOfPossibleValues)
}

export const getCategoricalNodeParameters = (
  node: CategoricalNode,
  linkedNode: TKNode | undefined,
) => {
  let dfCpd = node.data.originalNode.properties.df_cpd
  let parentList = node.data.originalNode.properties.parents_lst

  if (linkedNode) {
    if ("df_cpd" in linkedNode.properties) {
      dfCpd = linkedNode.properties.df_cpd
      parentList = linkedNode.properties.parents_lst ?? []
    } else {
      logger.error(
        `Linked node with id ${linkedNode.name} does not have df_cpd property for node ${node.id}`,
      )
      return []
    }
  }

  return Object.keys(dfCpd).filter((key) => {
    const isDistributionKey = key === "distribution"
    const isParentKey = parentList.includes(key)
    const isParameterKey = !isParentKey && !isDistributionKey

    return isParameterKey
  })
}

/**
 * Creates map of all possible values that parent nodes are taking.
 *
 * For example:
 *
 * node with df_cpd = { n1: ["foo", "foo", "bar"], n2: ["1", "2", "1"], ... }
 *
 * should output: {
 *  n1: ["foo", "bar"],
 *  n2: ["1", "2"]
 * }
 */
export const getNodeParentPossibleValues = (node: AppNode) => {
  if (node.type === NodeType.Group) return {}
  if (node.type === NodeType.Table) return {}
  if (node.type === NodeType.ForecastTable) return {}
  if (node.type === NodeType.ScenarioTable) return {}

  const parentPossibleValues: Record<string, unknown[]> = {}

  getParentsList(node).forEach((id) => {
    const uniqueValues = [...new Set(node.data.originalNode.properties.df_cpd[id] ?? [])]
    parentPossibleValues[id] = uniqueValues
  })

  return parentPossibleValues
}

/**
 * Creates map of all possible combinations of values that parent nodes can take.
 *
 * For example:
 *
 * values = { n1: ["foo", "bar"], n2: ["dog", "cat"] }
 *
 * should output: [
 *  { n1: "foo", n2: "dog" },
 *  { n1: "foo", n2: "cat" },
 *  { n1: "bar", n2: "dog" },
 *  { n1: "bar", n2: "cat" },
 * ]
 */
export const createParentValueCombinations = (values: Record<string, unknown[]>) => {
  const combinations: Record<string, unknown>[] = []
  const allParentIds = Object.keys(values)

  const createCombinations = (index: number, currentCombination: Record<string, unknown>) => {
    // went through all parent ids, so we have a full combination
    if (index === allParentIds.length) {
      combinations.push(currentCombination)
      return
    }

    const parentId = allParentIds[index]
    if (!parentId) return
    const currentParentPossibleValues = values[parentId] ?? []

    currentParentPossibleValues.forEach((possibleValue) =>
      createCombinations(index + 1, { ...currentCombination, [parentId]: possibleValue }),
    )
  }

  createCombinations(0, {})

  return combinations
}

export const getRowParentValues = (
  row: Record<string, unknown>,
  parentIds: string[],
): Record<string, unknown> => {
  const values: Record<string, unknown> = {}

  Object.entries(row).forEach(([key, value]) => {
    if (!parentIds.includes(key)) return

    values[key] = value
  })

  return values
}

export const getNodeTypeColor = (type: string) => {
  switch (type) {
    case "categorical":
      return "#5b4770"
    case "continuous":
      return "#53baa0"
    case "deterministic":
      return "#6986b0"
    case "discrete":
      return "#d6002a"
    case "forecast":
      return "lightsalmon"
    case "table":
      return "limegreen"
    case "scenario":
      return "lightpink"
    case "mixture":
      return "#f1a649"
    case "lag":
      return "gray"
    default:
      return "#cac8c8"
  }
}

export const isNodeDfCpdRowUsingParentAsDistributionParameter = (
  dfCpdRow: DfCpdRow,
  parentIds: string[],
) => {
  const parentIdsSet = new Set(parentIds)
  const rowValuesSet = new Set(Object.values(dfCpdRow))

  return rowValuesSet.intersection(parentIdsSet).size > 0
}
